import React, { useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { getAPIBaseURL } from "../services/helpers"
import { getData } from "../services/request"

const STATUS_LABELS = {
  APPLIED: "Applied",
  ELIGIBILITY_CONFIRMED: "Eligibility confirmed",
  INTERVIEW_IN_PROGRESS: "Interview in progress",
  SUBMITTED: "Interview submitted",
  UNDER_REVIEW: "Under review",
  SELECTED: "Selected",
  REJECTED: "Rejected",
  EXPIRED: "Expired"
}

const formatStatus = (rawStatus) => {
  const normalized = (rawStatus || "").toString().toUpperCase().replace(/\s+/g, "_")
  return STATUS_LABELS[normalized] || (rawStatus || "Unknown")
}

const formatDateTime = (iso) => {
  if (!iso) return "—"
  try {
    return new Date(iso).toLocaleString()
  } catch (e) {
    return iso
  }
}

const truncateMessage = (text, max = 160) => {
  if (!text) return ""
  if (text.length <= max) return text
  return `${text.slice(0, max).trim()}…`
}

const EmployerIndividualMessages = () => {
  const [applications, setApplications] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [statusFilter, setStatusFilter] = useState("ALL")

  useEffect(() => {
    const load = async () => {
      setLoading(true)
      setError("")
      const url = `${getAPIBaseURL()}/employme/employer/applicants?messageScope=INDIVIDUAL`
      const res = await getData(url, {}, {})
      setLoading(false)

      if (res.error === "") {
        if (res.result.status === 200) {
          setApplications(res.result.data?.data || [])
        } else {
          setError(res.result.data?.message || "Unable to load messages")
        }
      } else {
        setError(res.result?.data?.message || "Network error")
      }
    }

    load()
  }, [])

  const filteredApplications = useMemo(() => {
    if (statusFilter === "ALL") return applications
    const normalized = statusFilter.toUpperCase()
    return applications.filter((a) => (a.status || "").toString().toUpperCase() === normalized)
  }, [applications, statusFilter])

  return (
    <div className="rounded-lg bg-white shadow-sm border border-gray-200">
      <div className="border-b border-gray-200 bg-gray-50 px-4 py-4 sm:px-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Individual messages</h3>
            <p className="mt-1 text-xs text-gray-600">
              Accept, reject, and one-to-one updates sent to specific candidates.
            </p>
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-xs text-gray-900"
          >
            <option value="ALL">All statuses</option>
            {Object.entries(STATUS_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="p-4 sm:p-6">
        {error && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>
        )}
        {loading && <div className="text-sm text-gray-600">Loading messages...</div>}

        {!loading && !error && filteredApplications.length === 0 && (
          <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 p-6 text-sm text-gray-600">
            No individual messages yet. Send accept/reject or a personal update from an applicant&apos;s profile page.
          </div>
        )}

        {!loading && !error && filteredApplications.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500">Candidate</th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500">Job</th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500">Type</th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500">Sent</th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500">Message</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {filteredApplications.map((app) => (
                  <tr key={app._id} className="hover:bg-gray-50">
                    <td className="px-3 py-3 align-top">
                      <div className="font-medium text-gray-900">{app.candidate?.name || "Candidate"}</div>
                      <div className="text-xs text-gray-500">{app.candidate?.email || ""}</div>
                    </td>
                    <td className="px-3 py-3 align-top">
                      <div className="font-medium text-gray-900">{app.job?.title || "Job"}</div>
                      <div className="text-xs text-gray-500">{formatStatus(app.status)}</div>
                    </td>
                    <td className="px-3 py-3 align-top">
                      <span className="inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium uppercase text-slate-700">
                        {app.lastMessageType === "ACCEPT"
                          ? "Accepted"
                          : app.lastMessageType === "REJECT"
                            ? "Rejected"
                            : "Individual"}
                      </span>
                    </td>
                    <td className="px-3 py-3 align-top text-xs text-gray-500">
                      {formatDateTime(app.lastMessageAt || app.updatedAt)}
                    </td>
                    <td className="px-3 py-3 align-top">
                      <p className="max-w-xs text-xs text-gray-700 whitespace-pre-line">
                        {truncateMessage(app.resultMessage)}
                      </p>
                    </td>
                    <td className="px-3 py-3 align-top text-right">
                      {app.job?._id && (
                        <Link
                          to={`/employer/jobs/${app.job._id}/applicants/${app._id}`}
                          className="inline-flex rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                        >
                          View
                        </Link>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

export default EmployerIndividualMessages
